import { createSlice } from "@reduxjs/toolkit";

// Admin-console environment switch (Prod ⇄ Staging).
//
// The /admin console can point its data fetches at either the production worker
// (today's exact behaviour) or the staging worker so Will can debug/report
// against staging and see the same admin data. Staging's JWT_SECRET differs from
// prod's, so a prod-signed admin token 403s on staging — staging mode therefore
// holds its OWN admin token, minted by logging into staging once as the admin
// email. Prod mode keeps using the existing authSlice token unchanged.
//
// Persistence mirrors authSlice's authToken pattern (localStorage is the source
// of truth across reloads); the selected env + staging session survive a refresh
// so an admin does not re-log-in on every page load.
//
// INVARIANT: staging env is only ever valid while a staging token is held.
// "env=staging with no stagingToken" is the fetch-skipping limbo that stranded
// the console — every admin dashboard skips its fetch in that state (blank),
// and the login modal auto-reopens on reload, so the operator can't get back to
// prod. We make that state impossible: staging becomes the active env as a
// *consequence* of a successful staging login (setStagingSession), not a
// precondition the toggle commits before any session exists; and a persisted
// "staging" with no held token falls back to prod on load.

// Only trust a persisted "staging" selection if a staging token is also held —
// otherwise a failed/abandoned staging login from a previous session would
// re-strand the console on reload. Fall back to prod (always fetchable).
const persistedEnv = localStorage.getItem("adminEnv");
const persistedStagingToken = localStorage.getItem("stagingAuthToken");
const initialEnv =
  persistedEnv === "staging" && persistedStagingToken ? "staging" : "prod";

const adminEnvSlice = createSlice({
  name: "adminEnv",
  initialState: {
    env: initialEnv,
    stagingToken: persistedStagingToken || null,
    stagingEmail: localStorage.getItem("stagingEmail") || null,
  },
  reducers: {
    // Persist the selected environment. Guarded to the two valid values so a
    // stray dispatch can't wedge the console into an undefined env. Callers must
    // NOT dispatch "staging" without a held staging token (the toggle opens the
    // login dialog instead and lets setStagingSession commit the env) — that is
    // what keeps the "staging with no token" limbo unreachable.
    setAdminEnv: (state, { payload }) => {
      const env = payload === "staging" ? "staging" : "prod";
      state.env = env;
      localStorage.setItem("adminEnv", env);
    },
    // Store a freshly-minted staging admin session (token + the email it was
    // minted for) after a successful staging login, and commit the env to
    // staging in the same reducer. Committing here (rather than on toggle-click)
    // is what enforces the invariant: staging only becomes active once a token
    // exists, so a failed/cancelled login never leaves the console in staging.
    setStagingSession: (state, { payload }) => {
      const { token, email } = payload;
      state.stagingToken = token;
      state.stagingEmail = email;
      state.env = "staging";
      localStorage.setItem("stagingAuthToken", token);
      localStorage.setItem("stagingEmail", email);
      localStorage.setItem("adminEnv", "staging");
    },
    // Drop the held staging session (log out of staging / token expired) and
    // return to prod atomically — with no token, staging would be the limbo
    // state, so leaving the env on staging here would re-strand the console.
    clearStagingSession: (state) => {
      state.stagingToken = null;
      state.stagingEmail = null;
      state.env = "prod";
      localStorage.removeItem("stagingAuthToken");
      localStorage.removeItem("stagingEmail");
      localStorage.setItem("adminEnv", "prod");
    },
  },
});

export const { setAdminEnv, setStagingSession, clearStagingSession } =
  adminEnvSlice.actions;

export default adminEnvSlice;
